import type {
  AllowedOrigin,
  AllowedOriginsResponse,
  CreateAllowedOriginBody,
  UpdateAllowedOriginBody,
} from '@/api/(admin)/allowed-origins/types';
import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/**
 * Allowed origins — the domains a webshop embed may call our API from.
 *
 * Every endpoint is migrated to `/api/admin/allowed-origins`, so each call
 * takes `API_ROOT_URL`; `apiFetch`'s default base would put `/api-service` in
 * front of the path. Read by two features: its own management screen, and the
 * webshops roster, whose "attached domains" column resolves ids against this
 * list.
 */

/** The roster. Takes a pre-built query string; paging and filter JSON live in the hook. */
export async function fetchAllowedOrigins(
  qs: string,
): Promise<AllowedOriginsResponse> {
  return apiFetch<AllowedOriginsResponse>(
    `/api/admin/allowed-origins?${qs}`,
    undefined,
    API_ROOT_URL,
  );
}

export async function createAllowedOrigin(
  body: CreateAllowedOriginBody,
): Promise<AllowedOrigin> {
  const { data } = await apiFetch<{ data: AllowedOrigin }>(
    '/api/admin/allowed-origins',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
  return data;
}

export async function updateAllowedOrigin(
  id: string,
  body: UpdateAllowedOriginBody,
): Promise<AllowedOrigin> {
  const { data } = await apiFetch<{ data: AllowedOrigin }>(
    `/api/admin/allowed-origins/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
  return data;
}

export async function deleteAllowedOrigin(id: string): Promise<void> {
  return apiFetch<void>(
    `/api/admin/allowed-origins/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    API_ROOT_URL,
  );
}

/** A DELETE on the collection, with the ids in the body. 204, no body. */
export async function bulkDeleteAllowedOrigins(ids: string[]): Promise<void> {
  return apiFetch<void>(
    '/api/admin/allowed-origins',
    { method: 'DELETE', body: JSON.stringify({ ids }) },
    API_ROOT_URL,
  );
}
